/**
 * Локальные исходящие — ещё не подтверждённые сервером (DEV-2492).
 *
 * pending-… — отправка в полёте: её подтверждает ответ REST или WS-событие, сторы
 * ищут pending- с тем же текстом. failed-… — не ушло: подтверждать его нечему.
 * Отдельный префикс нужен, иначе подтверждение повтора или сообщение коллеги с
 * тем же текстом (общий номер) «доставило» бы неотправленный пузырь.
 */
import { BOT_AUTHOR_TYPE } from '../types/messenger.types'

let seq = 0

/** Уникален и в одну миллисекунду: подборка ЖК уходит несколькими текстами разом. */
export const newPendingId = (): string => `pending-${Date.now()}-${++seq}`

export const toFailedId = (pendingId: string): string => pendingId.replace(/^pending-/, 'failed-')

export const isLocalFailed = (message: { id: string }): boolean => message.id.startsWith('failed-')

/** Есть только в памяти вкладки — в счёт сообщений сервера не входит. */
export const isLocalMessage = (message: { id: string }): boolean =>
  message.id.startsWith('pending-') || isLocalFailed(message)

/** Окно, в котором эхо сервера — та же попытка, что и пузырь: часы устройства и сервера расходятся. */
export const ECHO_WINDOW_MS = 5 * 60_000

const sameAttempt = (a: string, b: string): boolean =>
  Math.abs(Date.parse(a) - Date.parse(b)) <= ECHO_WINDOW_MS

/**
 * Неотправленный пузырь, который на самом деле дошёл: запрос упал, а сервер
 * сообщение отправил (Telegram досылает после таймаута). Своё эхо с тем же текстом
 * в коротком окне подтверждает его — иначе «Повторить» дало бы клиенту дубль.
 * -1 — такого нет. Своё ли эхо, решает вызывающий: автор знают только сторы.
 */
export function findDeliveredUnsent(
  messages: readonly { id: string; content: string; timestamp: string }[],
  content: string,
  serverTime: string,
): number {
  return messages.findIndex(
    (m) => isLocalFailed(m) && m.content === content && sameAttempt(m.timestamp, serverTime),
  )
}

/**
 * Индекс серверного исходящего, которым стал локальный пузырь: тот же текст в окне
 * попытки. -1 — такого нет. Вызывающий сопоставляет один к одному и подаёт только
 * новые свои исходящие: иначе прежнее такое же «Ок» стёрло бы неотправленное.
 */
export const deliveryIndex = (
  local: { content: string; timestamp: string },
  sent: readonly { content: string; timestamp: string }[],
): number =>
  sent.findIndex((m) => m.content === local.content && sameAttempt(m.timestamp, local.timestamp))

/**
 * Моё исходящее с сервера: личный аккаунт (автора нет) или я сам на общем номере и
 * в поддержке (DEV-2492). Ответ AI-ассистента — не моё, хоть сотрудника-автора у
 * него и нет (DEV-1676): иначе он «доставил» бы мой неотправленный пузырь.
 */
export const isOwnOutgoing = (
  message: { isFromMe?: boolean; authorType?: string; authorManagerId?: string },
  me?: string,
): boolean =>
  !!message.isFromMe &&
  message.authorType !== BOT_AUTHOR_TYPE &&
  (!message.authorManagerId || message.authorManagerId === me)
